<i18n>
{
    "ru": {
        "sort": "Сортировка",
        "filter": "Фильтр",
        "title": "Ароматы по годам выпуска",
        "description": "Хронология всех парфюмерных запусков от первого в истории аромата до новейших разработок нишевых марок.",
        "placeholder": "Введите год...",
        "sort_desc": "По убыванию",
        "sort_asc": "По возрастанию",
        "perfumes_one": "аромат",
        "perfumes_few": "аромата",
        "perfumes_many": "ароматов",
        "best_articles": "Лучшие статьи",
        "chained_pages": "Интересные подборки",
        "meta": {
            "title": "Ароматы по годам выпуска за всю историю парфюмерии на Aromo.ru",
            "description": "Хронология всех парфюмерных запусков от первого в истории аромата до новейших разработок нишевых марок. Мы собрали огромную коллекцию брендов и ароматов духов и отсортировали их по годам выпуска для вашего удобства. Выберите год производства духов и изучайте вместе с Aromo.ru"
        }
    }
}
</i18n>

<template>
    <div class="years-page">

        <block-price-panel />

        <div class="years-page__block">
            <PageBlock :content-visible="isDeviceDesktop">
                <Breadcrumb :items="breadcrumbs"/>
                <SectionCard :loaded="loaded">
                    <template #main>
                        <div class="years-page__content">
                            <PageBlock
                                :title="$t('title')"
                                :small-header="!isDeviceDesktop"
                                :no-margin="!isDeviceDesktop"
                                :max-rows="isDeviceDesktop ? 2 : 6"
                                :description="$t('description')"
                                class="years-page__title-name"
                                :heading-tag="'h1'" />
                        </div>
                    </template>
                </SectionCard>
            </PageBlock>

            <PageBlock :content-visible="isDeviceDesktop">
                <AromaBox :aromabox="yearsAromabox" />
            </PageBlock>

            <PageBlock :content-visible="isDeviceDesktop">
                <CatalogList
                    class="years-page__catalog"
                    content-visible
                    :grid="isDeviceDesktop"
                    :sortInSearchField="isDeviceDesktop"
                    :grid-columns="3"
                    :regions-description="isDeviceDesktop"
                    id="licenseholders_catalog"
                    :placeholder="$t('placeholder')"
                    :search-loading="queryPending"
                    @search="searchQuery = $event"
                    @reset-all="resetAll"
                    :query="searchQuery"
                    :items="catalogItems"
                    :with-pagination="parseInt(catalogLastPage) > 1"
                    :sort-options="sortOptions"
                    v-model="catalogPage"
                    :page-count="catalogLastPage"
                    :sort-order="sortOrder"
                    :with-default-sort-order="false"
                    @change-sort-type="onChangeSortOrder"
                    :sort-type="sortOrder">

                    <template #item="{ item }" >

                        <BlockRow
                            :border="isDeviceDesktop"
                            :name="get(item, 'name')"
                            :url="get(item, 'htmlUrl')"
                            :caption="perfumesDeclension(get(item, 'perfumes.total', 0))"/>

                    </template>
                </CatalogList>
            </PageBlock>
            <PageBlock v-if="showRelatedArticles" :title="$t('best_articles')">
                <div class="perfume__page-block">
                    <ArticleRowItem
                        :key="index + article.code"
                        v-for="(article, index) in relatedArticles"
                        :data="article"/>
                </div>
            </PageBlock>
            <PageBlock v-if="showRelatedPages" :title="$t('chained_pages')">
                <div class="perfume__page-block">
                    <LinkedPagesList :items="relatedPages"/>
                </div>
            </PageBlock>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import get from 'lodash/get';
import url from '@/filters/url';

import Year from '@/models/catalog/Year';
import Breadcrumb from '@/components/layout/nav/Breadcrumb';
import BlockRow from '@/components/catalog/BlockRow';
import PageBlock from '@/components/layout/AppPageBlock';
import SectionCard from '@/components/layout/AppSectionCard';
import CatalogList from '@/components/catalog/CatalogList';
import ArticleRowItem from '@/components/layout/blocks/ArticlesList/ArticleRowItem';
import LinkedPagesList from '@/components/layout/blocks/LinkedPagesList';
import BlockPricePanel from '@/components/catalog/BlockPricePanel';
import AromaBox from '@/components/catalog/aromabox';

import { CatalogListMixin, getApiQueryParams } from '@/mixins/catalog/catalogList.mixin';

export default {
    name: 'Years',
    components: {
        LinkedPagesList,
        ArticleRowItem,
        CatalogList,
        SectionCard,
        PageBlock,
        BlockRow,
        Breadcrumb,
        BlockPricePanel,
        AromaBox
    },
    mixins: [CatalogListMixin],
    asyncData({ app, query, route, error }) {
        const years = new Year;
        const requestParams = {
            ...getApiQueryParams(query), 
            page: url.getPageNumber(route.path)
        };

        const requests = [
            years.params(requestParams).get(),
            years.relatedArticles().get(),
            years.relatedPages().get(),
            app.$api.get('/years/aroma-boxes')
        ];

        return Promise.all(requests).then(results => {
            const baseUrl = app.i18n.__baseUrl;
            const catalogHref = app.router.resolve({
                name: 'Years'
            }).href;
            const breadcrumbs = [
                {
                    path: baseUrl,
                    name: app.i18n.t('breadcrumbs.home_page'),
                },
                {
                    path: baseUrl + catalogHref,
                    name: app.i18n.t('breadcrumbs.years'),
                }
            ];

            const catalogPage = requestParams.page;
            const searchQuery = getApiQueryParams(query).name;
            const sortOrder = getApiQueryParams(query).order || 'desc';

            const relatedArticles = results[1];
            const relatedPages = results[2];

            const pageData = get(results, '[0][0]', {});
            const totalItems = get(results, '[0][0].total', 0);
            const catalogItems = get(results, '[0][0].items', []);
            const catalogLastPage = get(results, '[0][0].lastPage', 1);

            const yearsAromabox = get(results, '[3].data', []);

            if (process.server) {
                app.store.dispatch('rest/headers/filterLastModified', {
                    url: [`/years`]
                });
            }

            return {
                breadcrumbs,
                relatedArticles,
                relatedPages,
                catalogHref,
                catalogPage,
                catalogLastPage,
                catalogItems,
                searchQuery,
                sortOrder,
                pageData,
                totalItems,
                nextPage: get(results, '[0][0].nextPage'),
                prevPage: get(results, '[0][0].prevPage'),
                yearsAromabox
            };
        }).catch((e) => {
            error({statusCode: e.response.status, message: e.response.data});
        });

    },
    data() {
        return {
            catalogQueryObject: new Year,
            searchQuery: null,
            loaded: false,
            queryPending: false,
            catalogItems: [],
            relatedArticles: [],
            relatedPages: [],
            breadcrumbs: [],
            catalogPage: 1,
            catalogLastPage: 1,
            totalItems: 0,
            sortOrder: 'desc',
            sortOptions: {
                desc: {
                    name: this.$t('sort_desc')
                },
                asc: {
                    name: this.$t('sort_asc')
                }
            },
            filterIsShowed: false,
            nextPage: null,
            prevPage: null,
            yearsAromabox: []
        }
    },
    computed: {
        ...mapGetters(['isDeviceDesktop']),
        showRelatedPages() {
            return this.relatedPages && this.relatedPages.length && !this.isDeviceDesktop
        },
        showRelatedArticles() {
            return this.relatedArticles && this.relatedArticles.length && !this.isDeviceDesktop
        },
        metaDefault() {
            return {
                title: this.$t('meta.title'),
                description: this.$t('meta.description')
            };
        }
    },
    mounted() {
        this.loaded = true;
    },
    methods: {
        perfumesDeclension(count) {
            const declensionArray = [
                this.$t('perfumes_one'),
                this.$t('perfumes_few'),
                this.$t('perfumes_many')
            ];
            return this.$options.filters.numberDeclension(count, declensionArray)
        },
        get
    },
    head() {
        const meta = [
            {
                hid: 'description',
                name: 'description',
                content: this.$t('meta.description')
            }
        ]

        if(this.catalogPage > 1)
            meta.push({
                hid: 'robots',
                name: 'robots',
                content: 'noindex, follow'
            })

        return {
            title: this.$t('meta.title') + this.pageHeadCounter,
            meta
        }
    }
};
</script>
<style lang="less">
.years-page {
    @{desktop} & {
        margin-bottom: rem(85);
    }

    &__title-name {
        margin-bottom: 0;

        @{desktop} & {
            --desktop-header-padding-top: rem(20);
            --desktop-header-padding-bottom: rem(12);
        }
    }

    &__catalog {
        @{desktop} & {
            --grid-item-height: auto;
            margin-top: rem(40);
        }

        .catalog-list__pagination-wrapper {
            @{desktop} & {
                margin-top: rem(20);
            }
        }
    }

    .block-row__name {
        max-width: 20%;
    }

    .block-row__caption {
        white-space: nowrap;

        max-width: 80%;
    }

    .search-field__field_with-sort{
        @{desktop} & {
            max-width: 74%;
        }
    }

}
</style>
